import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/noop';

import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import roleIcon from '@adsk/bim360-matrix-react-assets/role-icon.svg';
import companyIcon from '@adsk/bim360-matrix-react-assets/company-icon.svg';
import {
  AssigneeTypes,
  EMAIL_REGEXP,
  DEFAULT_AVATAR,
  NO_AVATAR_SUFFIX
} from '@adsk/bim360-matrix-react-helpers/es/consts';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import './_AssigneeAvatar.scss';

/**
 * AssigneeAvatar component that displays the avatar of the assignee.
 **/
class AssigneeAvatar extends Component {
  constructor(props) {
    super(props);

    this.state = { changingAvatar: false };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.avatar !== nextProps.avatar) {
      this.setState({ changingAvatar: true });
    }
  }

  getAvatarImg(src) {
    return (
      <img
        onLoad={() => {
          this.setState({ changingAvatar: false });
        }}
        style={{ visibility: this.state.changingAvatar ? 'hidden' : 'inherit' }}
        className="AssigneeAvatar__avatar"
        src={src}
      />
    );
  }

  render() {
    const {
      avatar,
      type,
      className,
      name,
      onMouseOut,
      onMouseOver,
      onClick,
      onMouseDown,
      style,
      showTooltip,
      ...otherProps
    } = this.props;
    const noAvatar = !avatar || avatar.endsWith(NO_AVATAR_SUFFIX);
    const classes = classNames(
      'AssigneeAvatar',
      { 'AssigneeAvatar--users-avatar': type === AssigneeTypes.USER },
      { 'AssigneeAvatar--no-avatar': noAvatar },
      className
    );
    let displayedAvatar;

    if (!noAvatar) {
      displayedAvatar = this.getAvatarImg(avatar);
    } else if (type === AssigneeTypes.USER) {
      let initials = 'NA';

      // Check if name exists and is not an email (otherwise cannot create initials out of it)
      if (name && !EMAIL_REGEXP.test(name)) {
        const nameParts = name.split(' ');
        if (nameParts.length > 1) {
          initials = `${nameParts.shift()[0]}${nameParts.pop()[0]}`;
        } else {
          initials = nameParts.pop()[0];
        }
        displayedAvatar = (
          <div className="AssigneeAvatar__initials">
            <div className="AssigneeAvatar__initials-text">{initials}</div>
          </div>
        );
      } else {
        displayedAvatar = this.getAvatarImg(DEFAULT_AVATAR);
      }
    } else if ([AssigneeTypes.COMPANY, AssigneeTypes.ROLE].includes(type)) {
      displayedAvatar = (
        <SvgIcon width="20px" height="20px">
          {type === AssigneeTypes.ROLE ? roleIcon : companyIcon}
        </SvgIcon>
      );
    } else {
      displayedAvatar = <div />;
    }

    const avatarElement = (
      <div
        className={classes}
        style={style}
        onMouseOut={onMouseOut}
        onMouseOver={onMouseOver}
        onClick={onClick}
        onMouseDown={onMouseDown}
        {...otherProps}
      >
        {displayedAvatar}
      </div>
    );

    if (!showTooltip) {
      return avatarElement;
    }

    return (
      <Tooltip content={name} theme={Tooltip.Themes.LIGHT} place={Tooltip.Places.BOTTOM}>
        {avatarElement}
      </Tooltip>
    );
  }
}
AssigneeAvatar.defaultProps = {
  onMouseOver: noop,
  onMouseOut: noop,
  onClick: noop,
  onMouseDown: noop
};

AssigneeAvatar.propTypes = {
  /**
   * The name to show in the tooltip, and in case the type is a USER and avatar was not provided - show initials of the name instead of the avatar
   */
  name: PropTypes.string.isRequired,
  /**
   * The type of the assignee
   */
  type: PropTypes.oneOf(objectValues(AssigneeTypes)).isRequired,
  /**
   * A string represents the url of the avatar image
   */
  avatar: PropTypes.string,
  /**
   * A custom className for the component container
   */
  className: PropTypes.string,
  /**
   * Style object
   */
  style: PropTypes.object,
  /**
   * Whether to show tooltip
   */
  showTooltip: PropTypes.bool,
  /**
   * Function to execute on hover
   */
  onMouseOver: PropTypes.func,
  /**
   * Function to execute on mouse out
   */
  onMouseOut: PropTypes.func,
  /**
   * Function to execute on click
   */
  onClick: PropTypes.func,
  /**
   * Function to execute on mouse down
   */
  onMouseDown: PropTypes.func
};

AssigneeAvatar.defaultProps = {
  style: {},
  showTooltip: false
};

export default AssigneeAvatar;
